// src/services/syncService.ts

/**
 * Servicio para manejar la sincronización de datos offline
 */

interface SyncResult {
  success: boolean;
  message: string;
  timestamp?: Date;
  noConnection?: boolean; // ✅ NUEVO: Indica si falló por conectividad
}

/**
 * Inicia la sincronización de datos pendientes
 * @returns Promise con el resultado de la operación
 */
export const triggerSync = (): Promise<SyncResult> => {
  return new Promise((resolve) => {
    // Comprobar si el navegador está en línea
    if (!navigator.onLine) {
      resolve({
        success: false,
        message: "Sin conexión a internet. Conéctate para sincronizar.",
        noConnection: true
      });
      return;
    }

    // Comprobar si el service worker está disponible
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      resolve({
        success: false,
        message: "Service Worker no disponible. Intenta recargar la aplicación."
      });
      return;
    }

    let resolved = false; // Flag para evitar resolver múltiples veces

    // ✅ NUEVO: Manejar múltiples tipos de respuesta
    const messageListener = (event: MessageEvent) => {
      if (resolved) return; // Ya se resolvió

      // Sincronización completada con éxito
      if (event.data && event.data.type === 'SYNC_COMPLETED') {
        resolved = true;
        navigator.serviceWorker.removeEventListener('message', messageListener);
        resolve({
          success: true,
          message: event.data.message || "Sincronización completada con éxito",
          timestamp: new Date()
        });
      }
      
      // ✅ NUEVO: Error de sincronización
      else if (event.data && event.data.type === 'SYNC_ERROR') {
        resolved = true;
        navigator.serviceWorker.removeEventListener('message', messageListener);
        resolve({
          success: false,
          message: event.data.error || "Error durante la sincronización",
          noConnection: event.data.noConnection || false,
          timestamp: new Date()
        });
      }
      
      // ✅ NUEVO: Duplicado detectado (también es éxito)
      else if (event.data && event.data.type === 'DUPLICATE_DETECTED') {
        resolved = true;
        navigator.serviceWorker.removeEventListener('message', messageListener);
        resolve({
          success: true,
          message: event.data.message || "Contenedor duplicado detectado y removido",
          timestamp: new Date()
        });
      }
    };

    // Registrar el listener
    navigator.serviceWorker.addEventListener('message', messageListener);

    // ✅ NUEVO: Timeout más largo (10s) para esperar respuesta del servidor
    setTimeout(() => {
      if (resolved) return; // Ya se resolvió
      
      resolved = true;
      navigator.serviceWorker.removeEventListener('message', messageListener);
      resolve({
        success: false,
        message: "Timeout: La sincronización está tomando más tiempo del esperado",
        timestamp: new Date()
      });
    }, 10000); // 10 segundos

    // Enviar mensaje al Service Worker
    navigator.serviceWorker.controller.postMessage({
      type: 'SYNC_NOW'
    });
  });
};

/**
 * Comprueba si hay elementos pendientes de sincronización
 * @returns Promise con el número de elementos pendientes
 */
export const getPendingSyncCount = async (): Promise<number> => {
  return new Promise((resolve) => {
    // Si el SW no está disponible, retornar 0
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      resolve(0);
      return;
    }

    // ✅ NUEVO: Usar MessageChannel para obtener items de la cola
    const messageChannel = new MessageChannel();
    let responded = false;

    messageChannel.port1.onmessage = (event) => {
      if (responded) return;
      responded = true;

      if (event.data && event.data.type === 'QUEUE_ITEMS') {
        const count = event.data.items ? event.data.items.length : 0;
        resolve(count);
      } else {
        resolve(0);
      }
    };

    // Timeout de 3 segundos
    setTimeout(() => {
      if (!responded) {
        responded = true;
        resolve(0);
      }
    }, 3000);

    // Solicitar items al SW
    navigator.serviceWorker.controller.postMessage(
      { type: 'GET_QUEUE_ITEMS' },
      [messageChannel.port2]
    );
  });
};

/**
 * Registra un callback para ser notificado cuando se completa una sincronización
 * @param callback Función a llamar cuando se completa la sincronización
 * @returns Función para eliminar el listener
 */
export const onSyncCompleted = (
  callback: (data: { message: string; timestamp: Date; success: boolean }) => void
): () => void => {
  if (!('serviceWorker' in navigator)) {
    return () => {}; // Noop function
  }

  const handleMessage = (event: MessageEvent) => {
    // ✅ Sincronización exitosa
    if (event.data && event.data.type === 'SYNC_COMPLETED') {
      callback({
        message: event.data.message || "Sincronización completada",
        timestamp: new Date(),
        success: true
      });
    }
    
    // ✅ NUEVO: Sincronización con error
    else if (event.data && event.data.type === 'SYNC_ERROR') {
      callback({
        message: event.data.error || "Error en sincronización",
        timestamp: new Date(),
        success: false
      });
    }
    
    // ✅ NUEVO: Duplicado detectado
    else if (event.data && event.data.type === 'DUPLICATE_DETECTED') {
      callback({
        message: event.data.message || "Duplicado detectado",
        timestamp: new Date(),
        success: true
      });
    }
  };

  navigator.serviceWorker.addEventListener('message', handleMessage);

  // Devolver función para eliminar el listener
  return () => {
    navigator.serviceWorker.removeEventListener('message', handleMessage);
  };
};

/**
 * ❌ ELIMINADO: setupAutoSync ya NO sincroniza automáticamente al detectar 'online'
 * Ahora la sincronización es 100% manual desde los botones de la UI
 */
// export const setupAutoSync = (): () => void => {
//   const handleOnline = () => {
//     console.log("Conexión recuperada, sincronizando automáticamente...");
//     triggerSync();
//   };
//   window.addEventListener('online', handleOnline);
//   return () => {
//     window.removeEventListener('online', handleOnline);
//   };
// };

/**
 * ✅ NUEVO: Verificar estado de conexión con el servidor
 * @returns Promise<boolean> true si el servidor está accesible
 */
export const checkServerConnection = async (): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    const response = await fetch('https://app.securecontainer-ec.com/api/health', {
      method: 'HEAD',
      signal: controller.signal,
      cache: 'no-cache'
    });

    clearTimeout(timeoutId);
    return response.ok || response.status < 500;
  } catch (error) {
    console.warn('Servidor no accesible:', error);
    return false;
  }
};

export default {
  triggerSync,
  getPendingSyncCount,
  onSyncCompleted,
  checkServerConnection, // ✅ NUEVO
  // setupAutoSync // ❌ ELIMINADO
};